import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, RotateCcw, ArrowLeft, Loader2, Wand2, Heart, Star, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { API, MASCOT_URL, mediaUrl } from "@/lib/chikku";

const fmt = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
};

export default function Player() {
  const { id } = useParams();
  const navigate = useNavigate();
  const introRef = useRef(null);
  const bodyRef = useRef(null);

  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [play, setPlay] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | intro | body
  const [introDur, setIntroDur] = useState(0);
  const [bodyDur, setBodyDur] = useState(0);
  const [introTime, setIntroTime] = useState(0);
  const [bodyTime, setBodyTime] = useState(0);
  const [preparing, setPreparing] = useState(false);
  const [ended, setEnded] = useState(false);

  const fetchPlay = useCallback(async () => {
    const { data } = await axios.get(`${API}/stories/${id}/play`);
    return data;
  }, [id]);

  const resetAudio = () => {
    setPlaying(false);
    setPhase("idle");
    setIntroTime(0);
    setBodyTime(0);
    setEnded(false);
  };

  useEffect(() => {
    let active = true;
    (async () => {
      setStatus("loading");
      try {
        const data = await fetchPlay();
        if (active) {
          setPlay(data);
          setStatus("ready");
        }
      } catch (e) {
        console.error(e);
        if (active) setStatus("error");
      }
    })();
    return () => { active = false; };
  }, [fetchPlay]);

  const startFromIntro = () => {
    const intro = introRef.current;
    const body = bodyRef.current;
    if (!body) return;
    setEnded(false);
    body.currentTime = 0;
    if (play.introAudioUrl && intro) {
      intro.currentTime = 0;
      setPhase("intro");
      intro.play().catch(() => toast.error("Tap once more to start the story."));
    } else {
      setPhase("body");
      body.play().catch(() => toast.error("Tap once more to start the story."));
    }
  };

  const togglePlay = () => {
    if (!playing) {
      if (phase === "idle") {
        startFromIntro();
      } else if (phase === "intro") {
        introRef.current?.play().catch(() => {});
      } else if (phase === "body") {
        bodyRef.current?.play().catch(() => {});
      }
    } else {
      introRef.current?.pause();
      bodyRef.current?.pause();
    }
  };

  const replay = () => startFromIntro();

  const newGreeting = async () => {
    if (preparing) return;
    setPreparing(true);
    introRef.current?.pause();
    bodyRef.current?.pause();
    try {
      const data = await fetchPlay();
      setPlay(data);
      resetAudio();
      setIntroDur(0);
      toast.success("Chikku thought of a brand-new greeting!");
    } catch (e) {
      toast.error("Could not prepare a new greeting.");
    } finally {
      setPreparing(false);
    }
  };

  const totalDur = introDur + bodyDur;
  const current = phase === "body" ? introDur + bodyTime : introTime;
  const progress = totalDur ? (current / totalDur) * 100 : 0;

  const seek = (e) => {
    if (!totalDur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const target = pct * totalDur;
    const intro = introRef.current;
    const body = bodyRef.current;
    if (!intro || !body) return;
    if (target <= introDur) {
      setPhase("intro");
      intro.currentTime = target;
      body.pause();
      if (playing) intro.play().catch(() => {});
    } else {
      setPhase("body");
      body.currentTime = target - introDur;
      intro.pause();
      if (playing) body.play().catch(() => {});
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="pointer-events-none absolute inset-0">
        <div className="animate-blob absolute -top-24 right-0 h-80 w-80 rounded-full bg-sky-200/60 blur-3xl" />
        <div className="animate-blob absolute bottom-0 -left-20 h-80 w-80 rounded-full bg-amber-200/50 blur-3xl" style={{ animationDelay: "4s" }} />
      </div>

      <div className="relative mx-auto max-w-2xl px-5 sm:px-8 py-6 sm:py-8 flex flex-col min-h-screen">
        <button
          onClick={() => navigate("/")}
          data-testid="back-button"
          className="self-start inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 font-heading font-bold text-slate-700 border-b-4 border-slate-200 shadow active:translate-y-1 active:border-b-0 transition-transform"
        >
          <ArrowLeft className="h-5 w-5" /> Back
        </button>

        {status === "loading" && (
          <div className="flex-1 grid place-items-center text-slate-500" data-testid="player-loading">
            <div className="text-center">
              <img src={MASCOT_URL} alt="" className="h-28 w-28 rounded-full mx-auto animate-floaty bg-amber-50" />
              <Loader2 className="h-8 w-8 animate-spin text-orange-400 mx-auto mt-4" />
              <p className="font-heading text-xl mt-3">Chikku is thinking of a greeting...</p>
            </div>
          </div>
        )}

        {status === "error" && (
          <div className="flex-1 grid place-items-center text-center" data-testid="player-error">
            <div>
              <img src={MASCOT_URL} alt="" className="h-28 w-28 rounded-full mx-auto animate-wiggle bg-amber-50" />
              <p className="font-heading text-2xl mt-4 text-slate-700">This story is having a nap.</p>
              <button
                onClick={() => navigate("/")}
                className="mt-5 rounded-full bg-orange-400 px-6 py-3 font-heading text-lg font-bold text-white border-b-4 border-orange-600 active:translate-y-1 active:border-b-0 transition-transform"
              >
                Choose another story
              </button>
            </div>
          </div>
        )}

        {status === "ready" && play && (
          <div className="flex-1 flex flex-col items-center justify-center gap-6 py-4" data-testid="player-screen">
            <div className="relative">
              <motion.img
                key={play.thumbnailUrl}
                src={play.thumbnailUrl}
                alt={play.title}
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 20 }}
                className="h-56 w-56 sm:h-64 sm:w-64 rounded-[2.5rem] object-cover border-8 border-white shadow-2xl shadow-orange-300/40"
              />
              <img
                src={MASCOT_URL}
                alt="Chikku narrator"
                data-testid="player-mascot"
                className={`absolute -bottom-6 -right-6 h-24 w-24 rounded-full border-4 border-white bg-amber-50 shadow-xl ${playing ? "animate-wiggle" : "animate-floaty"}`}
              />
            </div>

            <div className="flex flex-col items-center gap-2 mt-2">
              <span className="rounded-full bg-orange-100 text-orange-700 px-4 py-1 text-xs font-extrabold uppercase tracking-widest" data-testid="player-category">
                {play.category}
              </span>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-800 text-center leading-tight" data-testid="player-title">
                {play.title}
              </h1>
            </div>

            {/* AI greeting speech bubble */}
            <AnimatePresence mode="wait">
              <motion.div
                key={play.introText}
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10 }}
                className="relative max-w-md rounded-3xl bg-white border-4 border-sky-200 px-6 py-4 shadow-lg text-center"
                data-testid="player-intro-text"
              >
                {play.introSource === "ai" && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-amber-300 px-3 py-0.5 text-[11px] font-extrabold uppercase tracking-wide text-amber-900 shadow" data-testid="player-intro-badge">
                    <Sparkles className="h-3 w-3" /> Fresh greeting
                  </span>
                )}
                <p className="font-heading text-lg text-slate-700">“{play.introText}”</p>
              </motion.div>
            </AnimatePresence>

            {/* Progress */}
            <div className="w-full max-w-md mt-2">
              <div
                onClick={seek}
                data-testid="player-progress"
                className="h-5 w-full rounded-full bg-sky-100 cursor-pointer overflow-hidden border-2 border-sky-200"
              >
                <motion.div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-400" style={{ width: `${progress}%` }} />
              </div>
              <div className="flex justify-between mt-2 px-1 font-bold text-sm text-slate-400">
                <span data-testid="player-current-time">{fmt(current)}</span>
                <span data-testid="player-duration">{fmt(totalDur)}</span>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-6 mt-2">
              <button
                onClick={replay}
                data-testid="replay-button"
                aria-label="Replay from start"
                className="grid h-16 w-16 place-items-center rounded-full bg-white text-sky-500 border-b-4 border-sky-200 shadow-lg active:translate-y-1 active:border-b-0 transition-transform hover:scale-105"
              >
                <RotateCcw className="h-7 w-7" />
              </button>

              <motion.button
                onClick={togglePlay}
                data-testid="play-pause-button"
                aria-label={playing ? "Pause story" : "Play story"}
                whileTap={{ scale: 0.9 }}
                className="grid h-24 w-24 place-items-center rounded-full bg-orange-400 text-white border-b-[6px] border-orange-600 shadow-xl active:translate-y-1 active:border-b-0 transition-transform hover:scale-105"
              >
                {playing ? <Pause className="h-11 w-11 fill-white" /> : <Play className="h-11 w-11 fill-white ml-1" />}
              </motion.button>

              <button
                onClick={newGreeting}
                data-testid="new-intro-button"
                aria-label="Get a new greeting"
                className="grid h-16 w-16 place-items-center rounded-full bg-white text-amber-500 border-b-4 border-amber-200 shadow-lg active:translate-y-1 active:border-b-0 transition-transform hover:scale-105 disabled:opacity-60"
                disabled={preparing}
              >
                {preparing ? <Loader2 className="h-6 w-6 animate-spin" /> : <Wand2 className="h-7 w-7" />}
              </button>
            </div>
            <p className="text-sm font-bold text-slate-400 -mt-1">Replay · Play · New greeting</p>

            {/* Moral */}
            {play.moral && (
              <motion.div
                data-testid="player-moral"
                initial={false}
                animate={ended ? { scale: [1, 1.04, 1] } : {}}
                transition={{ duration: 0.6 }}
                className={`w-full max-w-md rounded-3xl px-6 py-5 border-4 text-center mt-2 transition-colors duration-500 ${
                  ended ? "bg-amber-50 border-amber-300 shadow-xl shadow-amber-300/40" : "bg-white/70 border-sky-100"
                }`}
              >
                <p className="font-heading text-sm font-bold uppercase tracking-widest text-orange-500 flex items-center justify-center gap-2">
                  {ended ? <Star className="h-4 w-4 fill-amber-400 text-amber-400" /> : <Heart className="h-4 w-4 fill-orange-400 text-orange-400" />}
                  Moral of the story
                </p>
                <p className="font-heading text-lg sm:text-xl text-slate-700 mt-2 leading-snug">{play.moral}</p>
              </motion.div>
            )}

            {/* Two-phase audio: greeting then story body */}
            {play.introAudioUrl && (
              <audio
                ref={introRef}
                src={mediaUrl(play.introAudioUrl)}
                preload="metadata"
                data-testid="player-intro-audio"
                onLoadedMetadata={(e) => setIntroDur(e.target.duration || 0)}
                onTimeUpdate={(e) => setIntroTime(e.target.currentTime)}
                onPlay={() => setPlaying(true)}
                onPause={() => { if (phase !== "body") setPlaying(false); }}
                onEnded={() => {
                  setPhase("body");
                  const b = bodyRef.current;
                  if (b) { b.currentTime = 0; b.play().catch(() => {}); }
                }}
              />
            )}
            <audio
              ref={bodyRef}
              src={mediaUrl(play.bodyAudioUrl)}
              preload="metadata"
              data-testid="player-audio"
              onLoadedMetadata={(e) => setBodyDur(e.target.duration || 0)}
              onTimeUpdate={(e) => setBodyTime(e.target.currentTime)}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={() => { setPlaying(false); setEnded(true); setPhase("idle"); }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
